import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router";

import {
  dispatchCompareCostInvalidate,
  dispatchWorkForChanged,
  getApiErrorCode,
  postWorkFor,
  useRefreshProfile,
  writeStoredWorkFor,
} from "@penn/web-common";
import { PennText } from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Cross-origin Work-For handoff. Staff open this from the internal band's
 * "open as this customer" link, in a new tab on the customer host.
 *
 * `sessionStorage` does not cross origins, so the URL is the only transport —
 * but the stored target is written from the SERVER's response, never from the
 * parameter, so a tampered link cannot put a chosen identity in the banner.
 */
const REFUSALS: Record<string, string> = {
  "work-for-user-not-found":
    "That customer is not available to you. They may be inactive, outside your region, or an internal account.",
  "work-for-not-permitted": "Your account cannot use Work For.",
  "work-for-use-user-id":
    "That address belongs to a registered user. Pick them from the Work For list instead.",
  "work-for-invalid-link": "This link is missing a valid customer reference.",
};

export const WorkForOpenPage = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const restoring = useAppSelector((s) => s.auth.restoring);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const canWorkFor = useAppSelector(
    (s) => s.auth.user?.role?.canWorkForOthers === true,
  );
  const refreshProfile = useRefreshProfile();

  const [code, setCode] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // The exchange must run once; effects re-run on every dependency settle.
  const startedRef = useRef(false);

  const workForUserId = params.get("workForUserId") ?? "";

  useEffect(() => {
    if (restoring || !isAuthenticated || !canWorkFor) return;
    if (startedRef.current) return;
    startedRef.current = true;

    if (!UUID_RE.test(workForUserId)) {
      setCode("work-for-invalid-link");
      return;
    }

    void (async () => {
      try {
        // The existing stateless validator. It already refuses an unpermitted
        // caller and an internal or out-of-region target.
        const state = await postWorkFor({ userId: workForUserId });
        const effective = state.effective;
        if (effective.kind !== "registered" || !effective.userId) {
          setCode("work-for-user-not-found");
          return;
        }
        writeStoredWorkFor({
          kind: "registered",
          userId: effective.userId,
          email: effective.email ?? "",
          name: [effective.firstName, effective.lastName]
            .filter(Boolean)
            .join(" ")
            .trim(),
          company: effective.companyName ?? undefined,
        });
        dispatchCompareCostInvalidate();
        dispatchWorkForChanged();
        await refreshProfile();
        setDone(true);
      } catch (err) {
        setCode(getApiErrorCode(err) ?? "work-for-failed");
      }
    })();
  }, [restoring, isAuthenticated, canWorkFor, workForUserId, refreshProfile]);

  if (restoring) return null;
  // Start the issuer flow immediately and come back to THIS url, query and
  // all: the handoff is an explicit intent, not a stray arrival at /login.
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          autoStart: true,
          from: { pathname: location.pathname, search: location.search },
        }}
      />
    );
  }
  // Not a refusal panel: without the permission there was never a handoff to
  // make, so this is an ordinary wrong-address visit.
  if (!canWorkFor) return <Navigate to="/" replace />;
  // The customer landing IS "/" on this host; there is no /home here.
  if (done) return <Navigate to="/" replace />;

  if (code) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <PennText as="h1" size="xl" align="center" font="semibold" className="mb-2">
            Could not open this customer
          </PennText>
          <PennText as="p" size="sm" align="center" className="mb-4">
            {REFUSALS[code] ?? "Work For could not be applied."}
          </PennText>
          {/* Named so a support conversation can be specific, and so this can
              never be mistaken for a page acting as the intended customer. */}
          <PennText as="p" size="xs" align="center">
            Reference: {code}
          </PennText>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <PennText as="p" size="sm" align="center">
        Opening the customer view…
      </PennText>
    </div>
  );
};
